import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton, Tooltip } from '@mui/material';
import styled from 'styled-components';

import BinIconSrc from '@/assets/icons/bin.svg';
import EyePasswordImgSrc from '@/assets/icons/eye.svg';
import EyePasswordCrossedSrc from '@/assets/icons/eye-crossed.svg';
import { InitiativeBudgetField } from '@/features/canvas/api/initiative_budgets';
import { ListStyledImage } from '@/features/canvas/components/InitiativesAccordionContent/Fields/InitiativeCustom/InitiativeCustom';
import { CHARACTER_LIMITS } from '@/features/canvas/constants/character-limits';
import { CharacterCounter } from '@/features/ui/components/CharacterCounter';
import { SmallActionButton } from '@/features/ui/components/SmallActionButton/SmallActionButton';

interface BudgetCustomTextFieldProps {
  field: InitiativeBudgetField;
  isInitiativeLockedAndIsNotOwner: boolean;
  onDelete: (fieldId: number) => void;
  onUpdate: (fieldId: number, textValue: string) => void;
  readOnly: boolean;
  isInCreationMode?: boolean;
  isLoading?: boolean;
  onCreateSubmit?: (title: string, textValue: string) => void;
  onToggleVisibility?: (field: InitiativeBudgetField, isVisible: boolean) => void;
}

export const BudgetCustomTextField = ({
  field,
  isInCreationMode = false,
  readOnly,
  isInitiativeLockedAndIsNotOwner,
  onUpdate,
  onDelete,
  onToggleVisibility,
  onCreateSubmit,
  isLoading,
}: BudgetCustomTextFieldProps) => {
  const { t } = useTranslation();
  const [isEditingValue, setIsEditingValue] = useState(false);
  const [title, setTitle] = useState(field.display_title || '');
  const [textValue, setTextValue] = useState(field.text_value || '');
  const [isDirty, setIsDirty] = useState(false);

  // Check if field is visible in popup
  const isFieldVisible = field.popup_show !== false;

  const handleSubmit = () => {
    if (isInCreationMode && onCreateSubmit) {
      if (title.trim() && title.length <= CHARACTER_LIMITS.BUDGET_TITLE) {
        onCreateSubmit(title.trim(), textValue);
      }
    } else if (isEditingValue && isDirty) {
      onUpdate(field.id, textValue);
      setIsEditingValue(false);
      setIsDirty(false);
    }
  };

  const handleCancel = () => {
    setIsEditingValue(false);
    setTitle(field.display_title || '');
    setTextValue(field.text_value || '');
    setIsDirty(false);
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    if (newValue.length <= CHARACTER_LIMITS.LARGE_FIELD) {
      setTextValue(newValue);
      setIsDirty(true);
    }
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    if (newValue.length <= CHARACTER_LIMITS.BUDGET_TITLE) {
      setTitle(newValue);
    }
  };

  const handleVisibilityToggle = () => {
    if (onToggleVisibility) {
      onToggleVisibility(field, !isFieldVisible);
    }
  };

  const isFieldEditable = !readOnly && !isInitiativeLockedAndIsNotOwner;

  if (isInCreationMode) {
    return (
      <CustomFieldContainer>
        <FieldWrapper>
          <TitleInputWrapper>
            <StyledInput
              type="text"
              value={title}
              placeholder={t('editor.sidebar.initiative.title_custom_field_placeholder')}
              onChange={handleTitleChange}
              readOnly={readOnly || isInitiativeLockedAndIsNotOwner}
              maxLength={CHARACTER_LIMITS.BUDGET_TITLE}
              required
            />
            <CharacterCounter
              currentLength={title.length}
              maxLength={CHARACTER_LIMITS.BUDGET_TITLE}
            />
          </TitleInputWrapper>
          <TextAreaWrapper>
            <StyledTextArea
              value={textValue}
              onChange={e => setTextValue(e.target.value.slice(0, CHARACTER_LIMITS.LARGE_FIELD))}
              placeholder={t('editor.sidebar.initiative.max_char_count', {
                count: CHARACTER_LIMITS.LARGE_FIELD,
              })}
              rows={3}
              maxLength={CHARACTER_LIMITS.LARGE_FIELD}
              disabled={isLoading}
              readOnly={readOnly || isInitiativeLockedAndIsNotOwner}
              onKeyDown={e => {
                if (e.key === 'Enter' && e.ctrlKey) {
                  handleSubmit();
                }
                if (e.key === 'Escape') handleCancel();
              }}
            />
            <CharacterCounter
              currentLength={textValue.length}
              maxLength={CHARACTER_LIMITS.LARGE_FIELD}
            />
          </TextAreaWrapper>
        </FieldWrapper>
        {!readOnly && !isInitiativeLockedAndIsNotOwner && (
          <ActionWrapper>
            <SmallActionButton
              text={t('editor.sidebar.initiative.save_button_title')}
              isLoading={isLoading}
              disabled={!title.trim() || title.length > CHARACTER_LIMITS.BUDGET_TITLE}
              onClick={handleSubmit}
            />
          </ActionWrapper>
        )}
      </CustomFieldContainer>
    );
  }

  return (
    <CustomFieldContainer>
      <FieldWrapper>
        {isEditingValue ? (
          <TextAreaWrapper>
            <StyledTextArea
              value={textValue}
              onChange={handleTextChange}
              rows={3}
              maxLength={CHARACTER_LIMITS.LARGE_FIELD}
              onKeyDown={e => {
                if (e.key === 'Enter' && e.ctrlKey) handleSubmit();
                if (e.key === 'Escape') handleCancel();
              }}
              disabled={isLoading}
              autoFocus
            />
            <CharacterCounter
              currentLength={textValue.length}
              maxLength={CHARACTER_LIMITS.LARGE_FIELD}
            />
          </TextAreaWrapper>
        ) : (
          <StyledTextArea
            value={textValue}
            onClick={() => isFieldEditable && setIsEditingValue(true)}
            placeholder={
              isFieldEditable
                ? t('editor.sidebar.initiative.max_char_count', {
                    count: CHARACTER_LIMITS.LARGE_FIELD,
                  })
                : ''
            }
            readOnly
            rows={3}
            disabled={!isFieldEditable}
          />
        )}
      </FieldWrapper>

      {!readOnly && (
        <ActionWrapper>
          {/* Eye icon for visibility toggle */}
          {onToggleVisibility && (
            <IconButton
              aria-label="toggle-hide"
              size="small"
              onClick={handleVisibilityToggle}
              disabled={isLoading}
            >
              <Tooltip
                title={
                  isFieldVisible
                    ? t('editor.sidebar.initiative.hide_field')
                    : t('editor.sidebar.initiative.show_field')
                }
                placement="top"
              >
                <ListStyledImage src={isFieldVisible ? EyePasswordImgSrc : EyePasswordCrossedSrc} />
              </Tooltip>
            </IconButton>
          )}

          {/* Delete button */}
          {!isInitiativeLockedAndIsNotOwner && (
            <IconButton
              aria-label="delete"
              size="small"
              onClick={() => onDelete(field.id)}
              disabled={isLoading}
            >
              <Tooltip title={t('editor.sidebar.initiative.delete_field')} placement="top">
                <ListStyledImage src={BinIconSrc} />
              </Tooltip>
            </IconButton>
          )}

          {/* Save button - always visible when not locked */}
          {!isInitiativeLockedAndIsNotOwner && (
            <SmallActionButton
              text={t('editor.sidebar.initiative.save_button_title')}
              isLoading={isLoading}
              disabled={!isEditingValue || !isDirty}
              onClick={handleSubmit}
            />
          )}
        </ActionWrapper>
      )}
    </CustomFieldContainer>
  );
};

const CustomFieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ActionWrapper = styled.div`
  margin-top: 0.5rem;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5rem;
`;

const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
`;

const TitleInputWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const TextAreaWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledInput = styled.input`
  outline: none;
  width: 100%;
  padding: 0 8px;
  border-radius: 0.4rem;
  border: 1px solid ${({ theme }) => theme.palette.brand.backgroundDialog};
  font-size: 13px;
  color: ${({ theme }) => theme.palette.brand.textPrimary};

  &:focus {
    border: 1px solid ${({ theme }) => theme.palette.brand.textSecondary};
  }

  &:disabled {
    opacity: 0.6;
  }
`;

const StyledTextArea = styled.textarea`
  width: 100%;
  border: 1px solid ${({ theme }) => theme.palette.brand.backgroundDialog};
  border-radius: 0.4rem;
  resize: vertical;
  font-size: 14px;
  padding: 0.5rem;
  color: ${({ theme }) => theme.palette.brand.textPrimary};
  background: transparent;
  font-family: inherit;
  outline: none;
  min-height: 80px;
  max-height: 200px;
  overflow-y: auto;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.palette.brand.backgroundDialog};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.palette.brand.textSecondary};
    border-radius: 3px;

    &:hover {
      background: ${({ theme }) => theme.palette.brand.textPrimary};
    }
  }

  &:focus-visible {
    border: 1px solid ${({ theme }) => theme.palette.brand.textSecondary};
    outline: none;
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }

  &::placeholder {
    color: ${({ theme }) => theme.palette.brand.textSecondary};
    font-style: italic;
  }
`;
